import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserCheck,
  Trophy,
  Filter,
  Search,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Star,
  Users,
  Award,
  Target,
  Activity,
  Calendar,
  MapPin,
  Briefcase,
  TrendingUp,
  Crown,
  Medal,
  GraduationCap,
  Clock
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { coachesApi } from '../../services/api';
import { useSite } from '../../contexts/SiteContext';
import { useSeason } from '../../contexts/SeasonContext';
import SeasonSelector from '../Common/SeasonSelector';

// Types for coaches
interface Coach {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  team: {
    name: string;
    city: string;
    abbreviation: string;
    sport_name: string;
  };
  position: 'Head Coach' | 'Assistant Coach' | 'Coordinator' | 'Specialist';
  age?: number;
  birth_date?: string;
  birth_place?: string;
  years_coaching: number;
  years_with_team?: number;
  previous_teams?: string[];
  college_background?: string;
  playing_career?: {
    sport: string;
    position?: string;
    years_played?: number;
    teams_played?: string[];
  };
  coaching_record?: {
    wins: number;
    losses: number;
    ties?: number;
    win_percentage: number;
    playoff_appearances?: number;
    playoff_wins?: number;
    playoff_losses?: number;
  };
  championships?: number;
  awards?: string[];
  specialties?: string[];
  philosophy?: string;
  contract_year?: number;
  salary?: string;
  status: 'active' | 'interim' | 'retired' | 'fired' | 'resigned';
  hire_date?: string;
}

const CoachesPage: React.FC = () => {
  const site = useSite();
  const { currentSeason } = useSeason();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [expandedCoach, setExpandedCoach] = useState<string | null>(null);

  // Fetch coaches from API
  const { data: coachesData, isLoading, error } = useQuery({
    queryKey: ['coaches', site.sport, selectedPosition, selectedStatus, currentSeason],
    queryFn: async () => {
      let response;
      const filters = { season: currentSeason };
      if (selectedStatus === 'active') {
        response = await coachesApi.getActive(filters);
      } else if (selectedPosition === 'head coach' || selectedPosition === 'head_coach') {
        response = await coachesApi.getHeadCoaches(filters);
      } else {
        response = await coachesApi.getAll(filters);
      }
      console.log('Coaches API response:', response);
      return response.data || [];
    },
  });

  // Transform API data to match component interface
  console.log('Coaches API data:', coachesData);
  console.log('Coaches API data length:', coachesData?.length);
  const coaches = {
    data: coachesData?.map((coach: any) => ({
      id: coach.id || Math.random().toString(),
      slug: coach.slug || coach.person?.slug || coach.id, // Add slug field
      first_name: coach.person?.first_name || coach.first_name || 'Unknown',
      last_name: coach.person?.last_name || coach.last_name || 'Coach',
      full_name: coach.person?.full_name || coach.full_name || `${coach.person?.first_name || ''} ${coach.person?.last_name || ''}`.trim() || 'Unknown Coach',
      team: {
        name: coach.current_team?.name || 'Free Agent',
        city: coach.current_team?.city || '',
        abbreviation: coach.current_team?.abbreviation || 'FA',
        sport_name: coach.current_team?.sport_name || site.sport || 'Sports'
      },
      position: coach.title || coach.position || 'Coach',
      age: coach.person?.age || coach.age,
      birth_date: coach.person?.birth_date || coach.birth_date,
      birth_place: coach.person?.birth_place || coach.birth_place,
      years_coaching: coach.years_experience || coach.coaching_experience_years || coach.years_coaching || 0,
      years_with_team: coach.years_with_current_team,
      previous_teams: coach.previous_teams || [],
      college_background: coach.college || coach.alma_mater,
      playing_career: coach.playing_experience && typeof coach.playing_experience === 'object' ? {
        sport: coach.playing_experience.sport || site.sport,
        position: coach.playing_experience.position,
        years_played: coach.playing_experience.years_played,
        teams_played: coach.playing_experience.teams || []
      } : undefined,
      coaching_record: coach.career_record ? {
        wins: coach.career_record.wins || 0,
        losses: coach.career_record.losses || 0,
        ties: coach.career_record.ties,
        win_percentage: coach.career_record.win_percentage || 0,
        playoff_appearances: coach.career_record.playoff_appearances,
        playoff_wins: coach.career_record.playoff_wins,
        playoff_losses: coach.career_record.playoff_losses
      } : undefined,
      championships: coach.championships_won || 0,
      awards: coach.awards || [],
      specialties: coach.specialization ? [coach.specialization] : coach.specialties ? coach.specialties.split(',').map((s: string) => s.trim()) : [],
      philosophy: coach.coaching_philosophy || '',
      contract_year: coach.contract_end_year,
      status: coach.is_head_coach ? 'active' : coach.is_active ? 'active' : 'inactive',
      hire_date: coach.hire_date
    })) || []
  };

  console.log('Coaches transformed data:', coaches);
  console.log('Coaches data array:', coaches?.data);
  console.log('Filtered coaches before filter:', coaches?.data?.length);
  const filteredCoaches = coaches?.data?.filter((coach: any) => {
    const matchesSearch = 
      coach.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.team.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.position.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || coach.team.sport_name.toLowerCase() === selectedSport;
    const matchesPosition = selectedPosition === 'all' || coach.position.toLowerCase().includes(selectedPosition.toLowerCase());
    const matchesTeam = selectedTeam === 'all' || coach.team.abbreviation === selectedTeam;
    const matchesStatus = selectedStatus === 'all' || coach.status === selectedStatus;
    return matchesSearch && matchesSport && matchesPosition && matchesTeam && matchesStatus;
  }) || [];
  
  console.log('Filtered coaches count:', filteredCoaches.length);
  console.log('Filtered coaches:', filteredCoaches);

  const sports = Array.from(new Set(coaches?.data?.map(c => c.team?.sport_name).filter(Boolean) || []));
  const positions = Array.from(new Set(coaches?.data?.map(c => c.position).filter(Boolean) || []));
  const teams = Array.from(new Set(coaches?.data?.map(c => c.team?.abbreviation).filter(Boolean) || []));

  const activeCoaches = filteredCoaches.filter(coach => coach.status === 'active');
  const retiredCoaches = filteredCoaches.filter(coach => coach.status === 'retired');
  const interimCoaches = filteredCoaches.filter(coach => coach.status === 'interim');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'interim': return 'text-warning';
      case 'retired': return 'text-gray-400';
      case 'fired': return 'text-error';
      case 'resigned': return 'text-secondary';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'interim': return 'badge-warning';
      case 'retired': return 'badge-secondary';
      case 'fired': return 'badge-error';
      case 'resigned': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getCoachIcon = (championships?: number, awards?: string[]) => {
    if (championships && championships > 2) return Crown;
    if (championships && championships > 0) return Trophy;
    if (awards && awards.length > 3) return Medal;
    return UserCheck;
  };

  const getCoachColor = (championships?: number, awards?: string[]) => {
    if (championships && championships > 2) return 'text-primary';
    if (championships && championships > 0) return 'text-success';
    if (awards && awards.length > 3) return 'text-secondary';
    return 'text-gray-400';
  };

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatRecord = (record?: Coach['coaching_record']) => {
    if (!record) return 'N/A';
    if (record.ties && record.ties > 0) {
      return `${record.wins}-${record.losses}-${record.ties}`;
    }
    return `${record.wins}-${record.losses}`;
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <UserCheck className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Coaching Staff Directory
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">ALL </span>
              <span className="neon-text">COACHES</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Comprehensive coaching profiles and records across all major professional sports
            </p>
          </div>

          {/* Season Selector */}
          <div className="flex justify-center mb-6">
            <SeasonSelector />
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8 overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search coaches, teams, or positions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                <div className="relative flex-1 sm:flex-initial">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedSport}
                    onChange={(e) => setSelectedSport(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Sports</option>
                    {sports.map(sport => (
                      <option key={sport} value={sport?.toLowerCase()}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedPosition}
                    onChange={(e) => setSelectedPosition(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Positions</option>
                    {positions.map(position => (
                      <option key={position} value={position}>
                        {position}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Teams</option>
                    {teams.map(team => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative flex-1 sm:flex-initial">
                  <Activity className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="interim">Interim</option>
                    <option value="retired">Retired</option>
                    <option value="fired">Fired</option>
                    <option value="resigned">Resigned</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Activity className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {activeCoaches.length}
              </div>
              <div className="text-sm text-gray-400">Active Coaches</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {filteredCoaches.reduce((sum, c) => sum + (c.championships || 0), 0)}
              </div>
              <div className="text-sm text-gray-400">Total Championships</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Clock className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {Math.round(filteredCoaches.reduce((sum, c) => sum + c.years_coaching, 0) / filteredCoaches.length || 0)}
              </div>
              <div className="text-sm text-gray-400">Avg Years Coaching</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {sports.length}
              </div>
              <div className="text-sm text-gray-400">Sports Covered</div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Coaches Grid */}
      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : error ? (
          <div className="glass-card p-12 text-center">
            <UserCheck className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-bold text-white mb-2">Error Loading Coaches</h3>
            <p className="text-gray-400">{(error as Error).message || 'An error occurred'}</p>
          </div>
        ) : filteredCoaches.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Coaches Found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredCoaches.map((coach) => {
              const CoachIcon = getCoachIcon(coach.championships, coach.awards);
              return (
                <motion.div
                  key={coach.id}
                  variants={item}
                  whileHover={{ scale: 1.02 }}
                  className="group"
                >
                  <div 
                    className="glass-card h-full overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer group-hover:shadow-[0_0_30px_rgba(0,255,136,0.15)]"
                  >
                    {/* Coach Header */}
                    <Link to={`/coaches/${coach.slug || coach.id}`} className="block p-6 hover:bg-white/5 transition-colors">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="relative flex-1 sm:flex-initial">
                            <div className="absolute inset-0 bg-primary/20 blur-xl" />
                            <div className="relative w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                              <CoachIcon className={`w-8 h-8 ${getCoachColor(coach.championships, coach.awards)}`} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="badge badge-primary text-xs">
                                {coach.team.sport_name}
                              </span>
                              <span className={`badge ${getStatusBadge(coach.status)} text-xs`}>
                                {coach.status.charAt(0).toUpperCase() + coach.status.slice(1)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-400">
                              {coach.position}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" />
                      </div>

                      {/* Coach Info */}
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary transition-colors">
                        {coach.full_name}
                      </h3>
                      
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-8 h-8 rounded bg-gradient-to-br from-gray-700 to-gray-800 border border-border flex items-center justify-center">
                          <span className="text-xs font-bold text-white">
                            {coach.team.abbreviation}
                          </span>
                        </div>
                        <span className="text-sm text-gray-400">
                          {coach.team.name}
                        </span>
                      </div>

                      {/* Key Stats/Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Age
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {coach.age || calculateAge(coach.birth_date) || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Years Coaching
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {coach.years_coaching}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Championships
                          </div>
                          <div className="text-sm font-semibold text-primary">
                            {coach.championships || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Win %
                          </div>
                          <div className="text-sm font-semibold text-secondary">
                            {coach.coaching_record?.win_percentage ? 
                              (coach.coaching_record.win_percentage * 100).toFixed(1) + '%' : 
                              'N/A'
                            }
                          </div>
                        </div>
                      </div>

                      {/* Record Display */}
                      {coach.coaching_record && (
                        <div className="mt-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                            Career Record
                          </div>
                          <div className="text-lg font-bold text-white">
                            {formatRecord(coach.coaching_record)}
                          </div>
                        </div>
                      )}

                      {/* Specialties Preview */}
                      {coach.specialties && coach.specialties.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Target className="w-4 h-4 text-primary" />
                            <span className="text-xs text-primary font-semibold">
                              Specialties
                            </span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {coach.specialties.slice(0, 2).join(', ')}
                            {coach.specialties.length > 2 && '...'}
                          </div>
                        </div>
                      )}
                    </Link>

                    {/* Expand Button */}
                    <div className="px-6 pb-4">
                      <button 
                        onClick={() => setExpandedCoach(
                          expandedCoach === coach.id ? null : coach.id
                        )}
                        className="w-full py-2 text-xs text-gray-400 hover:text-primary transition-colors flex items-center justify-center gap-1"
                      >
                        {expandedCoach === coach.id ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            Show More Details
                          </>
                        )}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedCoach === coach.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-border/50 bg-surface/20"
                        >
                          <div className="p-6 space-y-6">
                            {/* Personal Info */}
                            <div>
                              <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                Background
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                {coach.birth_place && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Birthplace
                                    </div>
                                    <div className="text-white font-semibold">
                                      {coach.birth_place}
                                    </div>
                                  </div>
                                )}
                                {coach.college_background && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      College
                                    </div>
                                    <div className="text-white font-semibold">
                                      {coach.college_background}
                                    </div>
                                  </div>
                                )}
                                {coach.years_with_team && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Years with Team
                                    </div>
                                    <div className="text-white font-semibold">
                                      {coach.years_with_team}
                                    </div>
                                  </div>
                                )}
                                {coach.hire_date && (
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Hired
                                    </div>
                                    <div className="text-white font-semibold">
                                      {new Date(coach.hire_date).toLocaleDateString()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Playing Career */}
                            {coach.playing_career && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Playing Career
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Sport
                                    </div>
                                    <div className="text-white font-semibold">
                                      {coach.playing_career.sport}
                                    </div>
                                  </div>
                                  {coach.playing_career.position && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Position
                                      </div>
                                      <div className="text-white font-semibold">
                                        {coach.playing_career.position}
                                      </div>
                                    </div>
                                  )}
                                  {coach.playing_career.years_played && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Years Played
                                      </div>
                                      <div className="text-white font-semibold">
                                        {coach.playing_career.years_played}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Coaching Record Details */}
                            {coach.coaching_record && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Coaching Record
                                </h4>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                      Regular Season
                                    </div>
                                    <div className="text-white font-semibold">
                                      {formatRecord(coach.coaching_record)}
                                    </div>
                                  </div>
                                  {coach.coaching_record.playoff_appearances && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Playoff Apps
                                      </div>
                                      <div className="text-white font-semibold">
                                        {coach.coaching_record.playoff_appearances}
                                      </div>
                                    </div>
                                  )}
                                  {coach.coaching_record.playoff_wins && coach.coaching_record.playoff_losses && (
                                    <div>
                                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                                        Playoff Record
                                      </div>
                                      <div className="text-white font-semibold">
                                        {coach.coaching_record.playoff_wins}-{coach.coaching_record.playoff_losses}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* All Specialties */}
                            {coach.specialties && coach.specialties.length > 0 && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Specialties
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {coach.specialties.map((specialty: any, index: number) => (
                                    <span key={index} className="badge badge-secondary text-xs">
                                      {specialty}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Awards */}
                            {coach.awards && coach.awards.length > 0 && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Awards & Honors
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {coach.awards.map((award: any, index: number) => (
                                    <span key={index} className="badge badge-secondary text-xs">
                                      {award}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Philosophy */}
                            {coach.philosophy && (
                              <div>
                                <h4 className="text-sm font-bold text-primary mb-3 uppercase tracking-wider">
                                  Coaching Philosophy
                                </h4>
                                <p className="text-sm text-gray-300 italic">
                                  "{coach.philosophy}"
                                </p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CoachesPage;